export const PRODUCT = {
  name: 'TopicTalk',
  alternativeName: 'Talk by Topic',
  tagline: 'Erst das Thema. Dann das Gespräch.',
  consentPolicyVersion: '2026-09-08.1',
  communityPolicyVersion: '2026-09-08.1',
  consentNoticeDigest: '4257fe990599bf73c18d6c258bfb63f046e455527ccf1fa4a05779de2743a3a6',
  minimumAge: 18,
} as const;

export const TOPIC_CATEGORIES = [
  { id: 'everyday', labelDe: 'Alltag & Leben', labelEn: 'Everyday life', adSensitivity: 'standard' },
  { id: 'relationships', labelDe: 'Beziehungen & Familie', labelEn: 'Relationships & family', adSensitivity: 'restricted' },
  { id: 'learning', labelDe: 'Wissen, Lernen & Sprachen', labelEn: 'Knowledge, learning & languages', adSensitivity: 'standard' },
  { id: 'career', labelDe: 'Beruf & Gründung', labelEn: 'Career & entrepreneurship', adSensitivity: 'standard' },
  { id: 'finance', labelDe: 'Finanzen', labelEn: 'Finance', adSensitivity: 'restricted' },
  { id: 'culture', labelDe: 'Kultur, Reisen & Hobbys', labelEn: 'Culture, travel & hobbies', adSensitivity: 'standard' },
  { id: 'society', labelDe: 'Gesellschaft, Politik & Religion', labelEn: 'Society, politics & religion', adSensitivity: 'sensitive' },
  { id: 'health', labelDe: 'Gesundheit & Wohlbefinden', labelEn: 'Health & wellbeing', adSensitivity: 'sensitive' },
  { id: 'therapy', labelDe: 'Coaching, Beratung & Therapie', labelEn: 'Coaching, counselling & therapy', adSensitivity: 'sensitive' },
  { id: 'other', labelDe: 'Anderes Thema', labelEn: 'Other topic', adSensitivity: 'restricted' },
] as const;

export type TopicCategoryId = (typeof TOPIC_CATEGORIES)[number]['id'];

export const SUPPORTED_LANGUAGES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'pl', label: 'Polski' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'العربية' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const SESSION_DURATIONS = [20, 30, 45, 60] as const;
export const SESSION_INTENTS = ['tell', 'listen', 'balanced'] as const;
export const SESSION_MODALITIES = ['audio_video', 'audio'] as const;
export const AD_PLACEMENTS = ['pre', 'live', 'post'] as const;

export const CONSENT_NOTICE_DE = {
  title: 'Einwilligung in Gesprächsanalyse und gezielte Werbung',
  summary:
    'TopicTalk analysiert Inhalte deiner Gespräche während der laufenden Session und platziert darauf basierend besonders passende, klar gekennzeichnete Werbung.',
  bullets: [
    'Die Analyse erfasst, was in der Session thematisch besprochen wird – nicht, ob Person A, B oder C etwas gesagt hat.',
    'Werbung wird für den gemeinsamen Sessionkontext ausgewählt und nicht aus einem dauerhaften Profil einer natürlichen Person.',
    'TopicTalk erstellt keine sessionsübergreifenden Personenprofile für Werbung.',
    'Audio, Video und Rohtranskripte werden von TopicTalk nicht dauerhaft gespeichert.',
    'Bereinigte, personenunzugeordnete Analyse-, Anzeigen- und Feedbackdaten können dauerhaft gespeichert werden, um Trefferqualität und Kampagnen zu verbessern.',
    'Die Einwilligung ist Voraussetzung für die Nutzung. Bei Ablehnung wird kein Konto angelegt und die App kann nicht genutzt werden.',
    'Nicht jugendfreie, sexuell explizite und rechtswidrige Nutzung ist untersagt. Zulässige Therapie-, Gesundheits- und Beratungsgespräche bleiben möglich.',
  ],
} as const;

export const CONSENT_NOTICE_EN = {
  title: 'Consent to conversation analysis and targeted advertising',
  summary:
    'TopicTalk analyses the content of your conversations during the current session and uses it to place particularly relevant, clearly labelled advertising.',
  bullets: [
    'The analysis captures what is being discussed in the session, not whether person A, B or C said it.',
    'Advertising is selected for the shared session context, not from a persistent profile of an individual.',
    'TopicTalk does not create cross-session personal profiles for advertising.',
    'TopicTalk does not store audio, video or raw transcripts permanently.',
    'Cleaned analysis, ad and feedback data that is not assigned to a person may be stored permanently to improve match quality and campaigns.',
    'Consent is required to use TopicTalk. If you decline, no account is created and the app cannot be used.',
    'Adult, sexually explicit and unlawful use is prohibited. Permitted therapy, health and counselling conversations remain possible.',
  ],
} as const;

export const CONSENT_NOTICE_DIGESTS = {
  de: '4257fe990599bf73c18d6c258bfb63f046e455527ccf1fa4a05779de2743a3a6',
  en: 'ce42d1fa71cdfa54afdbddd684b5d67bfffecfbae883f8310f8aeed8adb2ddfa',
} as const;

export const CONSENT_NOTICES = {
  de: CONSENT_NOTICE_DE,
  en: CONSENT_NOTICE_EN,
} as const;
